/**
 * Session control for one machine, in place: a switch that asks first (the hooks go into that
 * machine's Claude Code settings), and the two confirmations, shared with Settings, Session
 * control. Admins and the owner change it; everyone else reads the state.
 */
import { useState } from 'react';
import type { ControlMachineState, Id } from '@orgus/protocol';
import { CONTROL_DEFAULT_WAIT_SECONDS } from '@orgus/protocol';
import { ConfirmDialog, Switch, Tooltip, toast } from '@orgus/ui';
import { useCan } from '../auth/useMe.ts';
import { setControl } from './live.ts';
import { machineSummary, waitLabel } from './model.ts';

/** Turn on and Turn off, each asked first. `change` throws so the dialog shows the error inline. */
export function ControlConfirmDialogs({
  name,
  m,
  confirm,
  onClose,
  change,
}: {
  name: string;
  m: ControlMachineState | undefined;
  confirm: 'on' | 'off' | null;
  onClose: () => void;
  change: (on: boolean, waitSeconds?: number) => Promise<void>;
}) {
  return (
    <>
      <ConfirmDialog
        open={confirm === 'on'}
        onOpenChange={(o) => !o && onClose()}
        title={`Turn on session control for ${name}?`}
        description={
          <>
            Orgus adds its hooks to the Claude Code settings on {name}, next to yours, so approvals, budget stops, policy and messages reach every Claude Code session
            there, including ones Orgus did not start. Sessions that start from now on are controlled; running ones from their next prompt or tool call. If Orgus is
            down, sessions carry on with their own permission prompts.
          </>
        }
        confirmLabel="Turn on session control"
        tone="primary"
        onConfirm={() => change(true, m?.waitSeconds ?? CONTROL_DEFAULT_WAIT_SECONDS)}
      >
        {m && <p className="font-mono text-caption text-fg-subtle">{m.settingsFile}</p>}
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm === 'off'}
        onOpenChange={(o) => !o && onClose()}
        title={`Turn off session control for ${name}?`}
        description={
          <>
            Orgus takes its hooks out of the Claude Code settings on {name}. If nothing else changed the file, it comes back exactly as it was. Sessions there go back to
            being watched only: approvals in Orgus, budget stops and messages no longer reach them.
          </>
        }
        confirmLabel="Turn off session control"
        tone="danger"
        onConfirm={() => change(false)}
      >
        {m && <p className="font-mono text-caption text-fg-subtle">{m.settingsFile}</p>}
      </ConfirmDialog>
    </>
  );
}

/** Why the switch cannot change now, or null. */
export function controlBlocked(o: { canChange: boolean; online: boolean; name: string; m: ControlMachineState | undefined }): string | null {
  if (!o.canChange) return 'Only admins and the owner can change session control.';
  if (!o.online) return `${o.name} is offline. Start Orgus there first.`;
  const s = machineSummary(o.m, o.online);
  if (!o.m?.installed && !s.canTurnOn) return s.detail;
  return null;
}

/** The switch, labelled for screen readers, with the reason in a tooltip when it cannot change. */
export function ControlSwitch({ machineId, name, online, m }: { machineId: Id; name: string; online: boolean; m: ControlMachineState | undefined }) {
  const canChange = useCan('admin');
  const [confirm, setConfirm] = useState<'on' | 'off' | null>(null);
  const installed = !!m?.installed;
  const blocked = controlBlocked({ canChange, online, name, m });

  const change = async (on: boolean, waitSeconds?: number) => {
    const r = await setControl(machineId, on, waitSeconds);
    if (!r.ok) throw new Error(r.message ?? 'The machine did not change session control.');
    toast(on ? `Session control is on for ${name}` : `Session control is off for ${name}`, { tone: 'success', description: r.message });
  };

  return (
    <>
      <Tooltip content={blocked ?? (installed ? `Requests wait ${waitLabel(m!.waitSeconds).toLowerCase()} for a person, then go back to the session` : undefined)}>
        <span tabIndex={blocked ? 0 : -1} className="inline-flex">
          <Switch checked={installed} disabled={!!blocked} onCheckedChange={(v) => setConfirm(v ? 'on' : 'off')} aria-label={`Session control for ${name}`} />
        </span>
      </Tooltip>
      <ControlConfirmDialogs name={name} m={m} confirm={confirm} onClose={() => setConfirm(null)} change={change} />
    </>
  );
}
